// noinspection PointlessBooleanExpressionJS
import React, {ReactNode} from "react";
import {Cell, Cells, CellType, Column, EvalData, ModelResults, ObjectDef, TaskDef, TaskGroupDef} from "../model";
import {InfoIcon} from "./info";
import * as XLSX from 'xlsx';
import {BookType} from 'xlsx';

const DoubleScrollbar = require("react-double-scrollbar");

interface ExtraFilterState {
    comparison?: "gte" | "lte";
    value?: string;
    selected?: string[];
}

interface PrecomputedFilter {
    column: string;
    format: string;
    comparison?: "gte" | "lte";
    filterVal?: number;
    parse?: (val: string) => number | null;
    words?: string[];
    selectedSet?: Set<string>;
}

interface TableProps {
    data: EvalData;
    mainMetric: string;
    tags: Set<string>;
}

interface TableState {
    sortId: string;
    filter: string;
    expanded: { [key: string]: boolean };
    closed: { [key: string]: boolean };
    extraFilters: { [column: string]: ExtraFilterState };
    activeFilterColumn: string | null;
}

const assignDefault = (val: any, def: any) => {
    return typeof val === "undefined" || val == null ? def : val;
}


export class TableView extends React.Component<TableProps, TableState> {

    private ranking: RankingHelper;
    private activeFilterRef: HTMLDivElement | null = null;
    private filterPopupAnchor: HTMLElement | null = null;
    private filterPopupEl: HTMLDivElement | null = null;

    constructor(props: Readonly<TableProps> | TableProps) {
        super(props);
        const expanded: { [key: string]: boolean } = {};
        (props.data?.taskGroups || []).forEach(val => expanded[val.id] = val.expanded || false);
        const closed: { [key: string]: boolean } = {};
        this.state = {
            sortId: `${RankingHelper.GLOBAL}_average`,
            expanded: expanded,
            closed: closed,
            filter: "",
            extraFilters: {},
            activeFilterColumn: null
        }
        this.ranking = new RankingHelper(props.data, props.mainMetric, props.tags, this.state);
    }

    static getDerivedStateFromProps(props: TableProps, state: TableState): Partial<TableState> | null {
        let expanded: { [key: string]: boolean } | null = null;
        for (const group of props.data?.taskGroups || []) {
            if (!(group.id in state.expanded)) {
                if (!expanded) expanded = {...state.expanded};
                expanded[group.id] = group.expanded || false;
            }
        }
        return expanded ? {expanded: expanded} : null;
    }

    private handleClickOutside = (e: MouseEvent) => {
        if (this.state.activeFilterColumn != null &&
            this.activeFilterRef &&
            !this.activeFilterRef.contains(e.target as Node) &&
            e.clientX < document.documentElement.clientWidth &&
            e.clientY < document.documentElement.clientHeight) {
            this.closeFilterPopup();
        }
    };

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        window.removeEventListener('scroll', this.updatePopupPosition, true);
    }

    private updatePopupPosition = () => {
        if (this.filterPopupAnchor && this.filterPopupEl) {
            const rect = this.filterPopupAnchor.getBoundingClientRect();
            this.filterPopupEl.style.top = `${rect.bottom + 4}px`;
            this.filterPopupEl.style.left = `${rect.left + rect.width / 2}px`;
        }
    };

    shouldComponentUpdate(nextProps: Readonly<TableProps>, nextState: Readonly<TableState>, nextContext: any): boolean {
        if (this.state.filter !== nextState.filter || this.state.activeFilterColumn !== nextState.activeFilterColumn) {
            /* DO NOTHING */
        } else {
            this.ranking = new RankingHelper(nextProps.data, nextProps.mainMetric, nextProps.tags, nextState);
        }
        return true;
    }

    componentDidUpdate(prevProps: Readonly<TableProps>, prevState: Readonly<any>, snapshot?: any) {
        this.updatePopupPosition();
    }

    private renderTable(): ReactNode {
        const topHeader = this.renderRow(this.ranking.topHeader(), true, -2, "topHeader");
        const mainHeader = this.renderRow(this.ranking.mainHeader(), true, -1, "mainHeader");
        const header = <thead>{topHeader}{mainHeader}</thead>;
        const rows = this.ranking.rows();
        const rowElements: ReactNode[] = [];
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const rowKey = row[0].rowid || row[0].value;
            rowElements.push(this.renderRow(row, false, i, rowKey));
        }
        const body = <tbody>{rowElements}</tbody>
        return <table>{header}{body}</table>;
    }

    private exportTable(event: any, format: BookType) {
        event.preventDefault();
        let res = [];
        res.push(this.exportRow(this.ranking.topHeader()));
        res.push(this.exportRow(this.ranking.mainHeader()));
        const rows = this.ranking.rows();
        for (const row of rows) {
            res.push(this.exportRow(row));
        }
        const book = XLSX.utils.book_new();
        const sheet = XLSX.utils.aoa_to_sheet(res);
        XLSX.utils.book_append_sheet(book, sheet, "export");
        XLSX.writeFile(book, `export.${format}`, {bookType: format})
    }

    private exportRow(cells: Cell[]) {
        return cells.map(val => this.exportCell(val)).flat();
    }

    private exportCell(cell: Cell) {
        const colspan = cell.colspan || 1;
        let value: string | number = cell.value.replace("\n", " ");
        if (value === RankingHelper.GLOBAL) {
            value = " ";
        }
        if (!isNaN(parseFloat(value))) {
            value = parseFloat(value);
        }
        if (colspan <= 1) {
            return value;
        } else {
            let res = Array(colspan).fill("")
            res[0] = value;
            return res;
        }
    }

    private renderRow(cells: Cell[], header: boolean, rowIdx: number, rowKey: string): ReactNode {
        const row = cells.map((val, idx) => this.renderCell(val, header, rowIdx, idx));
        return <tr key={rowKey}>{row}</tr>
    }

    private renderCell(c: Cell, header: boolean, rowIdx: number, colIdx: number): ReactNode {
        let clazz = `cell-${c.type.toLowerCase()} ${c.classes ? c.classes : ''}`;
        const key = `${rowIdx}_${colIdx}_${c.value}`;
        if (header) {
            if (c.type === CellType.FILTER_HEADER) {
                return this.renderFilterHeader(c, key);
            } else if (c.type === CellType.SORTABLE_HEADER) {
                return this.renderSortableColumn(c, key);
            } else if (c.type === CellType.HEADER_GROUP) {
                return this.renderHeaderGroupColumn(c, key);
            } else if (c.type === CellType.ROWID_HEADER) {
                return this.renderRowidHeader(c, key);
            } else {
                return <th colSpan={c.colspan || 1} className={clazz} key={key}>{c.value}</th>;
            }
        } else {
            if (c.type === CellType.ROWID) {
                return this.renderRowidCell(c, key);
            } else {
                return <td colSpan={c.colspan || 1} className={clazz} key={key}>{c.value}</td>;
            }
        }
    }

    private renderRowidHeader(c: Cell, key: string): ReactNode {
        let clazz = `cell-${c.type.toLowerCase()} ${c.classes ? c.classes : ''}`;
        const search = (
            <div className="rowid-filter">
                <span className="icon">🔍</span>
                <input type="text" placeholder="Filter models"
                       onChange={e => this.filter(e)} value={this.state.filter}>
                </input>
                <div className="export-panel">
                    <button onClick={e => this.exportTable(e, "xlsx")}>XLSX</button>
                    <button onClick={e => this.exportTable(e, "csv")}>CSV</button>
                    <button onClick={e => this.exportTable(e, "html")}>HTML</button>
                </div>
            </div>
        )
        return <td colSpan={c.colspan || 1} className={clazz} key={key}>{search}</td>
    }

    private renderRowidCell(c: Cell, key: string) {
        let clazz = `cell-${c.type.toLowerCase()} ${c.classes ? c.classes : ''}`;
        if (c.badge) {
            clazz += " cell-with-badge";
        }
        let value: string | ReactNode = c.value;
        if (c.url) {
            value = <a target="_blank" rel="noreferrer" href={c.url} className="model-name">{c.value}</a>
        }
        let desc = c.description ? <InfoIcon text={c.description}/> : null;
        let warn = c.warning ? <InfoIcon text={c.warning} warning={true}/> : null;
        let badge = c.badge ? <div className="badge">{c.badge}</div> : null;
        return <td colSpan={c.colspan || 1} className={clazz} key={key}>{value}{desc}{warn}{badge}</td>;
    }

    private renderHeaderGroupColumn(c: Cell, key: string): ReactNode {
        let clazz = `cell-${c.type.toLowerCase()} ${c.classes ? c.classes : ''} `;
        let expandable = assignDefault(this.props.data.options?.expandable, true);
        const expanded = expandable ? this.state.expanded[c.columnId!] : true;
        clazz += expanded ? "cell-expanded-true" : "cell-expanded-false";
        const url = c.url ? <a target="_blank" rel="noreferrer" href={c.url} className="circled-icon">🔗</a> : null;
        const tooltip = c.description ? <InfoIcon text={c.description}/> : null;
        const closeable = this.props.data.options?.closeable || false;
        const close = closeable ?
            <span className="circled-icon" onClick={() => this.closeGroup(c.columnId)}>✕</span> : null;
        let expander = null;
        if (expandable) {
            const expanderSymbol = expanded ? "-" : "+";
            const expanderClasses = "expander-symbol circled-icon";
            expander = <span className={expanderClasses}
                             onClick={() => this.toggleExpansion(c.columnId)}>{expanderSymbol}</span>;
        }
        return (
            <th colSpan={c.colspan || 1} className={clazz} key={key}>
                <div className="header-actions">
                    {expander}{tooltip}{url}{close}
                </div>
                <span>{c.value == RankingHelper.GLOBAL ? " " : c.value}</span>
            </th>
        );
    }

    private renderSortableColumn(c: Cell, key: string): ReactNode {
        let clazz = `cell-${c.type.toLowerCase()} ${c.classes ? c.classes : ''} `;
        let sortIndicator = <div className="sort-symbol">&nbsp;</div>;
        if (this.state.sortId === c.columnId) {
            clazz += " cell-sorted";
            sortIndicator = <div className="sort-symbol">▼</div>
        }
        return (
            <th colSpan={c.colspan || 1} className={clazz} onClick={() => this.sort(c.columnId)} key={key}>
                <span>{c.value}</span>
                {sortIndicator}
            </th>
        );
    }

    private renderFilterHeader(c: Cell, key: string): ReactNode {
        const columnName = c.columnId ? c.columnId.substring(RankingHelper.EXTRA_PREFIX.length) : "";
        const isOpen = this.state.activeFilterColumn === columnName;
        const hasFilter = this.hasActiveFilter(columnName);
        const iconClass = "circled-icon" + (hasFilter ? " filter-trigger-active" : "");
        return (
            <th className={`cell-empty ${c.classes || ''}`} key={key}
                style={{verticalAlign: 'top', paddingTop: '2px'}}>
                <div className="filter-icon-wrapper"
                     ref={isOpen ? (el: HTMLDivElement | null) => {
                         this.activeFilterRef = el;
                     } : undefined}
                     onClick={(e) => e.stopPropagation()}>
                    <span className={iconClass}
                          onClick={(e) => this.toggleFilterPopup(columnName, e)}></span>
                    {isOpen && this.renderFilterPopup(columnName)}
                </div>
            </th>
        );
    }

    private renderFilterPopup(columnName: string): ReactNode {
        const def = this.ranking.getColumnDef(columnName);
        if (!def || !this.filterPopupAnchor) return null;
        let content: ReactNode = null;
        switch (def.format) {
            case "number":
            case "size":
                content = this.renderNumberSizeFilter(columnName);
                break;
            case "string":
                content = this.renderStringFilter(columnName);
                break;
            case "nominal":
                content = this.renderNominalFilter(columnName);
                break;
        }
        if (!content) return null;
        const rect = this.filterPopupAnchor.getBoundingClientRect();
        return (
            <div ref={(el) => {
                this.filterPopupEl = el;
            }} style={{
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left + rect.width / 2,
                transform: 'translateX(-50%)',
                zIndex: 1010
            }}>
                {content}
            </div>
        );
    }

    private renderNumberSizeFilter(columnName: string): ReactNode {
        const filter = this.state.extraFilters[columnName] || {};
        const isGte = filter.comparison === "gte";
        const isLte = filter.comparison === "lte";
        return (
            <div className="filter-popup">
                <div className="filter-controls">
                    <button className={"filter-op" + (isGte ? " filter-op-active" : "")}
                            onClick={() => this.setComparison(columnName, isGte ? undefined : "gte")}>≥
                    </button>
                    <button className={"filter-op" + (isLte ? " filter-op-active" : "")}
                            onClick={() => this.setComparison(columnName, isLte ? undefined : "lte")}>≤
                    </button>
                    <input type="text" className="filter-input"
                           value={filter.value || ""}
                           onClick={(e) => e.stopPropagation()}
                           onChange={(e) => this.setFilterValue(columnName, e.target.value)}/>
                </div>
            </div>
        );
    }

    private renderStringFilter(columnName: string): ReactNode {
        const filter = this.state.extraFilters[columnName] || {};
        return (
            <div className="filter-popup">
                <div className="filter-controls">
                    <input type="text" className="filter-input filter-input-wide"
                           value={filter.value || ""}
                           onClick={(e) => e.stopPropagation()}
                           onChange={(e) => this.setFilterValue(columnName, e.target.value)}/>
                    <button className="filter-clear"
                            onClick={() => this.clearFilter(columnName)}>✕
                    </button>
                </div>
            </div>
        );
    }

    private renderNominalFilter(columnName: string): ReactNode {
        const filter = this.state.extraFilters[columnName] || {};
        const selected = new Set(filter.selected || []);
        const uniqueValues = this.ranking.getUniqueColumnValues(columnName);
        return (
            <div className="filter-popup">
                <div className="filter-controls filter-nominal">
                    <div className="filter-nominal-header">
                        <button className="filter-clear"
                                onClick={() => this.clearFilter(columnName)}>✕
                        </button>
                    </div>
                    <div className="filter-nominal-list">
                        {uniqueValues.map(val => (
                            <label key={val} className="filter-nominal-option">
                                <input type="checkbox"
                                       checked={selected.has(val)}
                                       onChange={() => this.toggleNominalValue(columnName, val)}/>
                                <span>{val}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    private toggleFilterPopup(columnName: string, event?: React.MouseEvent) {
        const isOpen = this.state.activeFilterColumn === columnName;
        if (!isOpen && event) {
            this.filterPopupAnchor = event.currentTarget as HTMLElement;
            window.addEventListener('scroll', this.updatePopupPosition, true);
        } else {
            this.filterPopupAnchor = null;
            window.removeEventListener('scroll', this.updatePopupPosition, true);
        }
        this.setState({...this.state, activeFilterColumn: isOpen ? null : columnName});
    }

    private closeFilterPopup() {
        if (this.state.activeFilterColumn != null) {
            this.filterPopupAnchor = null;
            window.removeEventListener('scroll', this.updatePopupPosition, true);
            this.setState({...this.state, activeFilterColumn: null});
        }
    }

    private hasActiveFilter(columnName: string): boolean {
        const filter = this.state.extraFilters[columnName];
        if (!filter) return false;
        const def = this.ranking.getColumnDef(columnName);
        if (!def) return false;
        switch (def.format) {
            case "number":
            case "size":
                return !!filter.comparison && !!filter.value;
            case "string":
                return !!filter.value;
            case "nominal":
                return !!filter.selected && filter.selected.length > 0;
        }
        return false;
    }

    private setComparison(columnName: string, comparison: "gte" | "lte" | undefined) {
        const extraFilters = {...this.state.extraFilters};
        extraFilters[columnName] = {...(extraFilters[columnName] || {}), comparison};
        this.setState({...this.state, extraFilters});
    }

    private setFilterValue(columnName: string, value: string) {
        const extraFilters = {...this.state.extraFilters};
        extraFilters[columnName] = {...(extraFilters[columnName] || {}), value: value || undefined};
        this.setState({...this.state, extraFilters});
    }

    private toggleNominalValue(columnName: string, value: string) {
        const extraFilters = {...this.state.extraFilters};
        const filter = {...(extraFilters[columnName] || {})};
        const selected = new Set(filter.selected || []);
        if (selected.has(value)) {
            selected.delete(value);
        } else {
            selected.add(value);
        }
        filter.selected = Array.from(selected);
        extraFilters[columnName] = filter;
        this.setState({...this.state, extraFilters});
    }

    private clearFilter(columnName: string) {
        const extraFilters = {...this.state.extraFilters};
        delete extraFilters[columnName];
        this.setState({...this.state, extraFilters});
    }

    private filter(event: any) {
        const text = event.target.value || "";
        const newState = {...this.state, filter: text};
        this.setState(newState);
        this.ranking.updateState(newState);
    }

    private sort(columnId?: string) {
        if (!columnId) return;
        const newState = {...this.state, sortId: columnId};
        this.ranking.sort(columnId);
        this.setState(newState);
    }

    private toggleExpansion(columnId?: string) {
        if (!columnId) return;
        let expanded = {...this.state.expanded};
        expanded[columnId] = !expanded[columnId];
        const newState = {...this.state, expanded: expanded};
        this.ranking.updateState(newState);
        this.setState(newState);
    }

    private closeGroup(columnId?: string) {
        if (!columnId) return;
        let closed = {...this.state.closed};
        closed[columnId] = !closed[columnId];
        const newState = {...this.state, closed: closed};
        this.ranking.updateState(newState);
        this.setState(newState);
    }

    render() {
        return <div className="table-wrapper"><DoubleScrollbar>{this.renderTable()}</DoubleScrollbar></div>;
    }
}

class RankingHelper {

    public static readonly GLOBAL = "__global__";
    public static readonly TASKS_WON = "__tasks_won__";
    public static readonly EXTRA_PREFIX = "__extra__";
    private static readonly SIZE_REGEX = /^([0-9]*\.?[0-9]+)\s*([kKmMbBtT]?)$/;
    private static readonly SIZE_MULTIPLIERS: { [key: string]: number } = {
        "": 1,
        "k": 1e3,
        "m": 1e6,
        "b": 1e9,
        "t": 1e12
    };

    private groups: RankingColumnGroup[];
    private models: RankingModel[];
    private extraColumns: string[];
    private columnDefs: { [key: string]: Column };
    private taskRanks: { [key: string]: string[] };

    constructor(private data: EvalData, private mainMetric: string, private tags: Set<string>, private tableState: TableState) {
        this.columnDefs = this.createColumnDefs(data);
        this.groups = this.createColumnGroups(data);
        this.models = this.createModels(data);
        this.extraColumns = this.createExtraColumns();
        this.computeAverages(data);
        this.taskRanks = this.computeTaskRanks();
        this.computeWinners();
        this.updateState(tableState);
    }

    public updateState(state: TableState) {
        this.tableState = state;
        const groups = [];
        for (const group of this.groups) {
            if (!state.closed[group.id]) {
                groups.push(group);
            }
        }
        if (groups.length !== this.groups.length) {
            this.groups = groups;
            this.computeAverages(this.data);
            this.taskRanks = this.computeTaskRanks();
            this.computeWinners();
        }
        if (state.sortId) {
            this.sort(state.sortId);
        }
        for (const group of this.groups) {
            group.expanded = state.expanded[group.id];
        }
    }

    public topHeader(): Cell[] {
        let res: Cell[] = [];
        res.push({value: "", type: CellType.ROWID_HEADER, classes: "cell-sticky"}); // models
        for (const column of this.extraColumns) {
            if (this.columnDefs[column]) {
                res.push({
                    value: "",
                    type: CellType.FILTER_HEADER,
                    columnId: RankingHelper.EXTRA_PREFIX + column,
                    classes: "cell-border-right"
                });
            } else {
                res.push(Cells.empty("cell-border-right"));
            }
        }
        if (assignDefault(this.data.options?.tasksWon, true)) {
            res.push(Cells.empty()); // tasks won
        }
        if (assignDefault(this.data.options?.average, true)) {
            res.push(Cells.empty("cell-border")); // global average
        }
        this.groups.forEach(group => res.push(group.groupCell()));
        return res;
    }

    public mainHeader(): Cell[] {
        let res: Cell[] = [];
        res.push({value: "Model", type: CellType.HEADER, classes: "cell-sticky"});
        for (const column of this.extraColumns) {
            const columnId = RankingHelper.EXTRA_PREFIX + column;
            if (this.columnDefs[column]) {
                res.push({
                    value: column,
                    type: CellType.SORTABLE_HEADER,
                    columnId: columnId,
                    classes: "cell-border-right"
                });
            } else {
                res.push({
                    value: column,
                    type: CellType.EXTRA_HEADER,
                    columnId: columnId,
                    classes: "cell-border-right"
                });
            }
        }
        if (assignDefault(this.data.options?.tasksWon, true)) {
            res.push({value: "Tasks won", type: CellType.SORTABLE_HEADER, columnId: RankingHelper.TASKS_WON});
        }
        if (assignDefault(this.data.options?.average, true)) {
            const avg = `Average\n(${this.tasks.length} categories)`;
            res.push({value: avg, type: CellType.SORTABLE_HEADER, classes: "cell-border", columnId: this.avgKey()});
        }
        for (const group of this.groups) {
            const avg = `Average\n(${group.tasks.length} categories)`;
            res.push({
                value: avg,
                type: CellType.SORTABLE_HEADER,
                classes: "cell-border",
                columnId: this.avgKey(group.id)
            });
            if (group.expanded) {
                group.tasks.forEach(task => {
                    res.push({value: task.name || task.id, type: CellType.SORTABLE_HEADER, columnId: task.id});
                });
            }
        }
        return res;
    }

    public rows(): Cell[][] {
        let filter = null;
        if (this.tableState.filter) {
            filter = this.tableState.filter.toLowerCase().split(/[^\p{L}\d]/u).filter(val => val.length > 0);
        }
        const extraFilters = this.precomputeExtraFilters();
        let res: Cell[][] = [];
        for (const model of this.models) {
            if (this.isRowVisible(model, filter, this.tags) && this.matchesExtraFilters(model, extraFilters)) {
                res.push(this.row(model));
            }
        }
        return res;
    }

    private isRowVisible(model: RankingModel, filter: string[] | null, tags: Set<string>) {
        if (tags.size > 0) {
            const modelTags = model.model.tags || [];
            let matches = false;
            for (const modelTag of modelTags) {
                if (tags.has(modelTag)) {
                    matches = true;
                    break;
                }
            }
            if (!matches) {
                return false;
            }
        }
        if (filter == null || filter.length === 0) return true;
        const name = (model.model.name || model.model.id).toLowerCase();
        for (const word of filter) {
            if (name.indexOf(word) < 0) {
                return false;
            }
        }
        return true;
    }

    private precomputeExtraFilters(): PrecomputedFilter[] {
        const extraFilters = this.tableState.extraFilters || {};
        const result: PrecomputedFilter[] = [];
        for (const column of Object.keys(extraFilters)) {
            const filterState = extraFilters[column];
            const def = this.columnDefs[column];
            if (!def || !filterState) continue;
            switch (def.format) {
                case "number":
                case "size": {
                    if (!filterState.comparison || !filterState.value) continue;
                    const parse = def.format === "size" ? RankingHelper.parseSize : RankingHelper.parseNumber;
                    const filterVal = parse(filterState.value);
                    if (filterVal == null) continue;
                    result.push({column, format: def.format, comparison: filterState.comparison, filterVal, parse});
                    break;
                }
                case "string": {
                    if (!filterState.value) continue;
                    const words = filterState.value.toLowerCase().split(/[^\p{L}\d]/u).filter(w => w.length > 0);
                    if (words.length === 0) continue;
                    result.push({column, format: "string", words});
                    break;
                }
                case "nominal": {
                    if (!filterState.selected || filterState.selected.length === 0) continue;
                    result.push({column, format: "nominal", selectedSet: new Set(filterState.selected)});
                    break;
                }
            }
        }
        return result;
    }

    private matchesExtraFilters(model: RankingModel, filters: PrecomputedFilter[]): boolean {
        for (const f of filters) {
            const rawValue = model.model.columns?.[f.column] ?? null;
            const strValue = rawValue != null ? String(rawValue) : "";
            switch (f.format) {
                case "number":
                case "size": {
                    const modelVal = f.parse!(strValue);
                    if (modelVal == null) return false;
                    if (f.comparison === "gte" && modelVal < f.filterVal!) return false;
                    if (f.comparison === "lte" && modelVal > f.filterVal!) return false;
                    break;
                }
                case "string": {
                    const cellValue = strValue.toLowerCase();
                    for (const word of f.words!) {
                        if (cellValue.indexOf(word) < 0) return false;
                    }
                    break;
                }
                case "nominal": {
                    if (!f.selectedSet!.has(strValue)) return false;
                    break;
                }
            }
        }
        return true;
    }

    public row(model: RankingModel): Cell[] {
        let res: Cell[] = [];
        res.push(model.rowidCell());
        for (const column of this.extraColumns) {
            res.push(model.extraColumnCell(column));
        }
        if (assignDefault(this.data.options?.tasksWon, true)) {
            res.push(model.tasksWonCell());
        }
        if (assignDefault(this.data.options?.average, true)) {
            res.push(model.valueCell(this.avgKey(), this.mainMetric, true));
        }
        for (const group of this.groups) {
            res.push(model.valueCell(this.avgKey(group.id), this.mainMetric, true));
            if (group.expanded) {
                group.tasks.forEach(task => {
                    res.push(model.valueCell(task.id, this.mainMetric, false, task.precision));
                });
            }
        }
        return res;
    }

    private createColumnDefs(data: EvalData): { [key: string]: Column } {
        const defs: { [key: string]: Column } = {};
        (data.columns || []).forEach(col => defs[col.name] = col);
        return defs;
    }

    private createColumnGroups(data: EvalData): RankingColumnGroup[] {
        const res: any = {};
        let list: RankingColumnGroup[] = [];
        (data.taskGroups || []).map(val => new RankingColumnGroup(val)).forEach(val => {
            if (!this.tableState.closed[val.id]) {
                res[val.id] = val;
                list.push(val);
            }
        });
        const globals = new RankingColumnGroup({id: RankingHelper.GLOBAL});
        res[globals.id] = globals;
        list.push(globals);
        (data.tasks || []).forEach(task => {
            if (task.groupId && res[task.groupId]) {
                res[task.groupId].add(task);
            } else if (task.groupId && this.tableState.closed[task.groupId]) {
                /* DO NOTHING */
            } else {
                res[RankingHelper.GLOBAL].add(task);
            }
        });
        list = list.filter(val => val.tasks.length > 0);
        return list;
    }

    private createModels(data: EvalData): RankingModel[] {
        const defs: any = {};
        (data.models || []).forEach(val => defs[val.id.toLowerCase()] = val);
        let res: RankingModel[] = [];
        (data.results || []).forEach(val => {
            const modelDef = defs[val.id.toLowerCase()];
            if (modelDef) {
                val = {...val, ...modelDef};
            }
            res.push(new RankingModel(val));
        });
        return res;
    }

    private createExtraColumns(): string[] {
        const columnsSet = new Set<string>();
        let res: string[] = [];
        for (const model of this.models) {
            if (!this.isRowVisible(model, null, this.tags) || !model.model.columns) continue;
            for (let key of Object.keys(model.model.columns)) {
                if (!columnsSet.has(key)) {
                    columnsSet.add(key);
                    res.push(key);
                }
            }
        }
        return res;
    }

    private computeAverages(data: EvalData) {
        const metrics: string[] = data.metrics.map(val => val.id);
        for (const group of this.groups) {
            const key = this.avgKey(group.id);
            const tasks = group.tasks.map(task => task.id);
            this.models.forEach(val => this.computeAveragesColumn(val, key, tasks, metrics));
        }
        const globalKey = this.avgKey();
        const allTasks = this.tasks.map(task => task.id);
        this.models.forEach(val => this.computeAveragesColumn(val, globalKey, allTasks, metrics));
    }

    private computeAveragesColumn(model: RankingModel, key: string, tasks: string[], metrics: string[]) {
        for (const metric of metrics) {
            let sum: number | null = 0.0;
            for (const task of tasks) {
                const taskResults = model.model.results[task] || {};
                const value = taskResults[metric];
                if (typeof value === 'undefined' || value == null) {
                    sum = null;
                    break;
                } else {
                    sum += value;
                }
            }
            if (sum != null) {
                sum = sum / tasks.length;
                let results = model.model.results[key] || {};
                results[metric] = sum;
                model.model.results[key] = results;
            }
        }
    }

    public sort(column: string) {
        if (column.startsWith(RankingHelper.EXTRA_PREFIX)) {
            const name = column.substring(RankingHelper.EXTRA_PREFIX.length);
            this.models.sort((a, b) => this.compareExtraColumn(a, b, name));
        } else {
            this.models.sort((a, b) => this.compare(a, b, column));
        }
    }

    private static nullableCompare(v1: number | null, v2: number | null, fallback: number): number {
        if (v1 == null && v2 == null) return fallback;
        if (v1 == null) return 1;
        if (v2 == null) return -1;
        const diff = v2 - v1;
        return diff !== 0 ? diff : fallback;
    }

    private compare(m1: RankingModel, m2: RankingModel, column: string) {
        const res1 = m1.model.results[column] || {};
        const res2 = m2.model.results[column] || {};
        let val1 = res1[this.mainMetric] || null;
        let val2 = res2[this.mainMetric] || null;
        if (column === RankingHelper.TASKS_WON) {
            val1 = m1.tasksWon;
            val2 = m2.tasksWon;
        }
        return RankingHelper.nullableCompare(val1, val2, m1.model.id.localeCompare(m2.model.id));
    }

    private compareExtraColumn(m1: RankingModel, m2: RankingModel, columnName: string): number {
        const def = this.columnDefs[columnName];
        if (!def) return 0;
        const raw1 = m1.model.columns?.[columnName] ?? null;
        const raw2 = m2.model.columns?.[columnName] ?? null;
        const str1 = raw1 != null ? String(raw1) : null;
        const str2 = raw2 != null ? String(raw2) : null;
        const empty1 = str1 == null || str1 === "";
        const empty2 = str2 == null || str2 === "";
        if (empty1 && empty2) return m1.model.id.localeCompare(m2.model.id);
        if (empty1) return 1;
        if (empty2) return -1;
        const fallback = m1.model.id.localeCompare(m2.model.id);
        switch (def.format) {
            case "number":
                return RankingHelper.nullableCompare(RankingHelper.parseNumber(str1!), RankingHelper.parseNumber(str2!), fallback);
            case "size":
                return RankingHelper.nullableCompare(RankingHelper.parseSize(str1!), RankingHelper.parseSize(str2!), fallback);
            case "string":
            case "nominal": {
                const cmp = str1!.toLowerCase().localeCompare(str2!.toLowerCase());
                return cmp !== 0 ? cmp : fallback;
            }
        }
    }

    private static parseNumber(val: string): number | null {
        const n = parseFloat(val);
        return isNaN(n) ? null : n;
    }

    private static parseSize(val: string): number | null {
        const match = val.trim().match(RankingHelper.SIZE_REGEX);
        if (!match) return null;
        const num = parseFloat(match[1]);
        if (isNaN(num)) return null;
        return num * (RankingHelper.SIZE_MULTIPLIERS[match[2].toLowerCase()] || 1);
    }

    public getColumnDef(column: string): Column | undefined {
        return this.columnDefs[column];
    }

    public getUniqueColumnValues(column: string): string[] {
        const values = new Set<string>();
        for (const model of this.models) {
            const val = model.model.columns?.[column];
            if (val != null && String(val) !== "") {
                values.add(String(val));
            }
        }
        return Array.from(values).sort();
    }

    private computeTaskRanks() {
        let taskRanks: { [key: string]: string[] } = {};
        let ids = this.tasks.map(task => task.id);
        this.groups.forEach(group => ids.push(this.avgKey(group.id)));
        ids.push(this.avgKey());
        for (const id of ids) {
            const models = [...this.models].filter(row => this.isRowVisible(row, null, this.tags));
            models.sort((a, b) => this.compare(a, b, id));
            taskRanks[id] = models
                .filter(val => typeof (val.model.results[id] || {})[this.mainMetric] === "number")
                .map(val => val.model.id);
        }
        return taskRanks;
    }

    private computeWinners() {
        const taskIds = this.tasks.map(val => val.id);
        for (const model of this.models) {
            model.computeWinners(this.taskRanks, taskIds);
        }
        const models = [...this.models];
        models.sort((a, b) => this.compare(a, b, RankingHelper.TASKS_WON));
        this.taskRanks[RankingHelper.TASKS_WON] = models.map(val => val.model.id);
        const otherColumnIds = this.groups.map(val => this.avgKey(val.id));
        otherColumnIds.push(this.avgKey());
        otherColumnIds.push(RankingHelper.TASKS_WON);
        for (const model of this.models) {
            model.computeAchievements(this.taskRanks, taskIds.concat(otherColumnIds));
        }
    }

    get tasks(): ObjectDef[] {
        let res: ObjectDef[] = [];
        this.groups.forEach(val => val.tasks.forEach(task => res.push(task)));
        return res;
    }

    private avgKey(groupId?: string) {
        return groupId ? `${groupId}_average` : `${RankingHelper.GLOBAL}_average`;
    }
}

class RankingColumnGroup {

    tasks: TaskDef[];
    expanded: boolean = true;

    constructor(public taskGroup: TaskGroupDef) {
        this.tasks = [];
    }

    get id(): string {
        return this.taskGroup.id;
    }

    get colspan() {
        return this.expanded ? this.tasks.length + 1 : 1;
    }

    groupCell(): Cell {
        return {
            value: this.taskGroup.name || this.taskGroup.id,
            type: RankingHelper.GLOBAL === this.taskGroup.name ? CellType.EMPTY : CellType.HEADER_GROUP,
            colspan: this.colspan,
            classes: "cell-border",
            columnId: this.id,
            url: this.taskGroup.url,
            description: this.taskGroup.description
        }
    }

    add(task: TaskDef) {
        this.tasks.push(task);
    }
}

class RankingModel {

    public tasksWon: number = 0;
    private achievements: any = {};

    constructor(public model: ModelResults) {
    }

    valueCell(column: string, metric: string, border: boolean = false, precision: number = 2): Cell {
        const results = this.model.results[column] || {};
        let value = results[metric];
        if (!value && value !== 0.0) {
            value = null;
        }
        const formatted = value != null ? value.toFixed(assignDefault(precision, 2)).toString() : "";
        let classNames = border ? "cell-border " : " ";
        const medal = this.achievements[column];
        if (medal) {
            classNames += medal;
        }
        return {
            value: formatted,
            type: CellType.VALUE,
            classes: classNames
        }
    }

    extraColumnCell(column: string): Cell {
        return {
            value: (this.model.columns || {})[column] || "",
            type: CellType.VALUE,
            classes: "cell-border-right cell-smaller"
        }
    }

    rowidCell(): Cell {
        return {
            value: this.model.name || this.model.id,
            type: CellType.ROWID,
            classes: "cell-sticky",
            description: this.model.description,
            url: this.model.url,
            warning: this.model.warning,
            rowid: this.model.id,
            badge: this.model.badge
        }
    }

    tasksWonCell(): Cell {
        const medal = this.achievements[RankingHelper.TASKS_WON] || "";
        return {
            value: this.tasksWon.toFixed(0).toString(),
            type: CellType.VALUE,
            classes: medal
        }
    }

    computeWinners(taskRanks: { [key: string]: string[] }, taskIds: string[]) {
        let tasksWon = 0;
        for (const taskId of taskIds) {
            const ranking = taskRanks[taskId] || [];
            const place = ranking.indexOf(this.model.id);
            if (place === 0) {
                tasksWon += 1;
            }
        }
        this.tasksWon = tasksWon;
    }

    computeAchievements(taskRanks: { [key: string]: string[] }, columnIds: string[]) {
        let achievements: any = {};
        let medalsClasses: any = {0: "cell-gold", 1: "cell-silver", 2: "cell-bronze"};
        for (const taskId of columnIds) {
            const ranking = taskRanks[taskId] || [];
            const place = ranking.indexOf(this.model.id);
            const medal = medalsClasses[place];
            if (medal) {
                achievements[taskId] = medal;
            }
        }
        this.achievements = achievements;
    }
}
